import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePlayer } from "../lib/player";
import { coverUri } from "../lib/api";
import { fmtClock } from "../lib/format";
import { Icon } from "./icons";

const SONG_LABEL = { off: "Repetir música: desligado", once: "Repetir música: mais uma vez", loop: "Repetir música: continuamente" };
const LIST_LABEL = { off: "Repetir lista: desligado", once: "Repetir lista: mais uma vez", loop: "Repetir lista: continuamente" };
const BADGE = { once: "1", loop: "∞" };

function Ctrl({ icon, label, onClick, active, testid, big, badge, mode, size, disabled }) {
  const s = big ? (size || 56) : 40;
  return (
    <button className={`icon-btn sp-ctrl ${big ? "sp-big" : ""}`} aria-label={label} title={label} onClick={onClick} disabled={disabled} data-testid={testid} aria-pressed={big ? undefined : !!active} data-mode={mode}
      style={{ position: "relative", width: s, height: s }}>
      <Icon name={icon} size={big ? Math.round(s * 0.42) : 19} color={big ? "var(--on-brand)" : active ? "var(--brand-primary)" : undefined} />
      {badge ? <span className="ctrl-badge">{badge}</span> : null}
      {active && !big ? <span className="sp-dot" /> : null}
    </button>
  );
}

function voiceLine(now) {
  if (now?.error) return now.error;
  if (!now) return "A carregar…";
  if (!now.part) return "Esta música não tem áudio disponível.";
  if (now.fallback) return `Sem faixa de ${now.wanted || "voz"} · a tocar ${now.part.voice}`;
  return now.part.voice;
}

function Seek({ testid }) {
  const { status, seek } = usePlayer();
  const [drag, setDrag] = useState(null);
  const t = drag ?? status.t;
  const pct = status.d ? (t / status.d) * 100 : 0;
  return (
    <div className="sp-seek">
      <span className="sp-time" data-testid={`${testid}-current`}>{fmtClock(t)}</span>
      <input type="range" min={0} max={status.d || 0} step={0.1} value={Math.min(t, status.d || 0)} aria-label="Posição da música" data-testid={testid}
        style={{ "--pct": `${pct}%` }}
        onChange={(e) => setDrag(Number(e.target.value))}
        onPointerUp={() => { if (drag !== null) { seek(drag); setDrag(null); } }}
        onKeyUp={() => { if (drag !== null) { seek(drag); setDrag(null); } }} />
      <span className="sp-time" data-testid={`${testid}-duration`}>{fmtClock(status.d)}</span>
    </div>
  );
}

function MainControls({ big = 64, prefix = "player" }) {
  const p = usePlayer();
  return (
    <div className="sp-controls">
      <Ctrl icon="shuffle" label="Aleatório" active={p.shuffle} onClick={p.toggleShuffle} testid={`${prefix}-shuffle`} />
      <Ctrl icon="play-back" label="Anterior" onClick={p.prev} testid={`${prefix}-prev`} />
      <Ctrl icon={p.status.playing ? "pause" : "play"} label={p.status.playing ? "Pausa" : "Reproduzir"} onClick={p.toggle} testid={`${prefix}-toggle`} big size={big} />
      <Ctrl icon="play-forward" label="Seguinte" onClick={p.next} testid={`${prefix}-next`} />
      <Ctrl icon="repeat" label={LIST_LABEL[p.repeatList]} active={p.repeatList !== "off"} badge={BADGE[p.repeatList]} mode={p.repeatList} onClick={p.cycleRepeatList} testid={`${prefix}-repeat-list`} />
    </div>
  );
}

function QueueList({ onPick }) {
  const p = usePlayer();
  const upcoming = p.order.slice(p.pos + 1);
  const Row = ({ qi, on }) => {
    const it = p.queue[qi];
    return (
      <button className={`sp-qrow ${on ? "on" : ""}`} onClick={() => { p.jumpTo(qi); onPick?.(); }} data-testid={`player-queue-item-${qi}`}>
        <img className="cover" src={coverUri(it.cover_url)} alt="" style={{ width: 40, height: 40, borderRadius: 4 }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="sp-qtitle">{it.title}</span>
          <span className="sp-qsub">{it.artist || ""}</span>
        </span>
        {on ? <Icon name="volume-high" size={16} color="var(--brand-primary)" /> : null}
      </button>
    );
  };
  return (
    <div data-testid="player-queue-sheet" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div className="sp-qhead">A tocar</div>
      {p.index >= 0 ? <Row qi={p.index} on /> : null}
      <div className="sp-qhead">A seguir{p.context?.label ? ` · ${p.context.label}` : ""}{p.context?.key?.startsWith("event:") || p.context?.key?.startsWith("setlist:") ? " (ordem oficial)" : ""}</div>
      {upcoming.length ? upcoming.map((qi) => <Row key={qi} qi={qi} />) : <div className="t-hint" style={{ padding: "6px 4px" }}>Fim da lista{p.repeatList !== "off" ? " — volta ao início (repetir lista)" : ""}.</div>}
    </div>
  );
}

/** Full-screen "A tocar" view (Spotify-style). */
function NowPlaying({ onClose }) {
  const p = usePlayer();
  const navigate = useNavigate();
  const [queue, setQueue] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const { now } = p;
  const cover = coverUri(now?.song?.cover_url || p.item.cover_url);
  const go = (path) => { onClose(); navigate(path); };
  return (
    <div className="sp-full ulc-fade" role="dialog" aria-label="A tocar" data-testid="now-playing">
      <div className="sp-full-bg" style={{ backgroundImage: `url(${cover})` }} />
      <div className="sp-full-inner">
        <div className="sp-full-top">
          <button className="icon-btn" aria-label="Minimizar leitor" onClick={onClose} data-testid="now-playing-close"><Icon name="chevron-down" size={26} /></button>
          <button className="sp-from" onClick={() => p.context?.path && go(p.context.path)} data-testid="now-playing-context">
            <span className="t-kicker" style={{ fontSize: 10 }}>A TOCAR DE</span>
            <span style={{ fontFamily: "var(--f-medium)", fontSize: 13 }}>{p.context?.label || "Repertório"}</span>
          </button>
          <button className="icon-btn" aria-label="Fechar leitor" onClick={() => { p.close(); onClose(); }} data-testid="player-close"><Icon name="close" size={22} /></button>
        </div>
        {queue ? (
          <div className="sp-full-queue"><QueueList /></div>
        ) : (
          <div className="sp-art-wrap"><img className="sp-art" src={cover} alt="" data-testid="now-playing-cover" /></div>
        )}
        <div className="sp-meta">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sp-title" data-testid="now-playing-title">{now?.song?.title || p.item.title}</div>
            <div className="sp-artist">{now?.song?.artist || p.item.artist || ""}</div>
            <div className="sp-voice" style={now?.fallback || (now && !now.part) ? { color: "var(--on-warning)" } : undefined} data-testid="now-playing-voice"><Icon name="mic" size={12} /> {voiceLine(now)}</div>
          </div>
          <Ctrl icon="repeat-one" label={SONG_LABEL[p.repeatSong]} active={p.repeatSong !== "off"} badge={BADGE[p.repeatSong]} mode={p.repeatSong} onClick={p.cycleRepeatSong} testid="player-repeat-song" />
        </div>
        <Seek testid="now-playing-seek" />
        <MainControls big={68} prefix="player" />
        <div className="sp-bottom">
          <button className="chip" onClick={() => go(`/song/${p.item.song_id}`)} data-testid="now-playing-song"><Icon name="document-text" size={14} /> LETRA E PARTITURA</button>
          <button className={`chip ${queue ? "active" : ""}`} onClick={() => setQueue((v) => !v)} data-testid="player-queue"><Icon name="list" size={14} /> FILA</button>
        </div>
        {p.nextItem && !queue ? <div className="t-hint" style={{ textAlign: "center" }} data-testid="player-next-title">A seguir: {p.nextItem.title}</div> : null}
      </div>
    </div>
  );
}

export function MiniPlayer() {
  const p = usePlayer();
  const [full, setFull] = useState(false);
  const [wide, setWide] = useState(() => window.matchMedia("(min-width: 900px)").matches);
  const [queueOpen, setQueueOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 900px)");
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  useEffect(() => { if (!p?.item) setFull(false); }, [p?.item]);
  if (!p?.item) return null;
  const { now, status } = p;
  const title = now?.song?.title || p.item.title;
  const cover = coverUri(now?.song?.cover_url || p.item.cover_url);
  const pct = status.d ? (status.t / status.d) * 100 : 0;
  const sub = (
    <span className="mini-sub" style={now?.fallback || (now && !now.part) ? { color: "var(--on-warning)" } : undefined} data-testid="player-voice">
      {voiceLine(now)}{p.context?.label ? ` · ${p.context.label}` : ""}
    </span>
  );

  if (wide) {
    return (
      <div className="mini-player sp-bar" data-testid="mini-player" role="region" aria-label="Leitor de áudio">
        <button className="sp-bar-left" onClick={() => setFull(true)} data-testid="player-expand" aria-label="Abrir leitor">
          <img className="cover" src={cover} alt="" style={{ width: 56, height: 56, borderRadius: 6 }} />
          <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            <span className="mini-title" data-testid="player-title">{title}</span>
            {sub}
            {p.nextItem ? <span className="mini-sub" data-testid="player-next-title-bar">A seguir: {p.nextItem.title}</span> : null}
          </span>
        </button>
        <div className="sp-bar-center">
          <MainControls big={40} prefix="mini-player" />
          <Seek testid="player-seek" />
        </div>
        <div className="sp-bar-right">
          <Ctrl icon="repeat-one" label={SONG_LABEL[p.repeatSong]} active={p.repeatSong !== "off"} badge={BADGE[p.repeatSong]} mode={p.repeatSong} onClick={p.cycleRepeatSong} testid="bar-repeat-song" />
          <Ctrl icon="list" label="Fila" active={queueOpen} onClick={() => setQueueOpen((v) => !v)} testid="bar-queue" />
          <Ctrl icon="expand" label="Ecrã completo" onClick={() => setFull(true)} testid="bar-fullscreen" />
          <Ctrl icon="close" label="Fechar leitor" onClick={p.close} testid="bar-close" />
        </div>
        {queueOpen ? <div className="sp-queue-pop"><QueueList /></div> : null}
        {full ? <NowPlaying onClose={() => setFull(false)} /> : null}
      </div>
    );
  }

  return (
    <div className="mini-player sp-mini" data-testid="mini-player" role="region" aria-label="Leitor de áudio">
      <div className="sp-mini-card">
        <button className="sp-mini-info" onClick={() => setFull(true)} data-testid="player-expand" aria-label="Abrir leitor">
          <img className="cover" src={cover} alt="" style={{ width: 40, height: 40, borderRadius: 4, flexShrink: 0 }} />
          <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
            <span className="mini-title" data-testid="player-title">{title}</span>
            {sub}
          </span>
        </button>
        <button className="icon-btn" aria-label="Anterior" onClick={p.prev} data-testid="mini-player-prev" style={{ width: 36, height: 36 }}><Icon name="play-back" size={18} /></button>
        <button className="icon-btn" aria-label={status.playing ? "Pausa" : "Reproduzir"} onClick={p.toggle} data-testid="mini-player-toggle" style={{ width: 40, height: 40 }}><Icon name={status.playing ? "pause" : "play"} size={24} /></button>
        <button className="icon-btn" aria-label="Seguinte" onClick={p.next} data-testid="mini-player-next" style={{ width: 36, height: 36 }}><Icon name="play-forward" size={18} /></button>
        <div className="sp-mini-progress" data-testid="player-seek-mini"><div style={{ width: `${pct}%` }} /></div>
      </div>
      {full ? <NowPlaying onClose={() => setFull(false)} /> : null}
    </div>
  );
}
